import { useState, type CSSProperties } from "react";
import { Button } from "@legends/ui";
import LobbyRenderer from "./LobbyRenderer";
import { monoLabelStyle } from "./sections";
import type { LobbyPreviewBundle } from "./lobbyPreviewBus";

// The shared preview surface (ADR 0134) rendered by BOTH the same-browser preview
// window (BroadcastChannel-fed) and the cross-device page (poll-fed). It renders
// the real LobbyRenderer from a bundle, with its own guest / ticket-holder toggle
// and a live/connection status. Desktop fills the window; phone is a centered
// device frame (the lobby's container queries reflow to the phone composition).

export interface LobbyPreviewShellProps {
  bundle: LobbyPreviewBundle | null;
  mode: "desktop" | "phone";
  /** True while updates are flowing (channel open / device polling ok). */
  connected: boolean;
  /** Short label for where the bundle comes from ("This browser" / "Live"). */
  statusLabel?: string;
  /** Extra note under a waiting/disconnected state. */
  waitingNote?: string;
}

const bar: CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 5,
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 10,
  padding: "8px 12px",
  background: "var(--surface-quiet)",
  borderBottom: "1px solid var(--hairline)",
};

export default function LobbyPreviewShell({ bundle, mode, connected, statusLabel, waitingNote }: LobbyPreviewShellProps) {
  const [as, setAs] = useState<"guest" | "holder">("guest");
  const holder = as === "holder";
  const owned = new Set(holder && bundle ? bundle.tickets.map((t) => t.id) : []);

  return (
    <main style={{ minHeight: "100vh", background: "var(--surface-base)" }}>
      <div style={bar}>
        <span style={{ ...monoLabelStyle, color: "var(--gold)" }}>
          {mode === "phone" ? "Phone preview" : "Desktop preview"}
        </span>
        {bundle && <span style={{ fontSize: "var(--fs-body-2)", color: "var(--text-hi)" }}>{bundle.caveName}</span>}
        <div role="group" aria-label="Preview as" style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
          <Button size="sm" variant={as === "guest" ? "accent" : "muted"} aria-pressed={as === "guest"} onClick={() => setAs("guest")}>
            As guest
          </Button>
          <Button size="sm" variant={as === "holder" ? "accent" : "muted"} aria-pressed={as === "holder"} onClick={() => setAs("holder")}>
            As ticket-holder
          </Button>
        </div>
        <span
          role="status"
          style={{ ...monoLabelStyle, color: connected ? "var(--success-hi)" : "var(--text-dim)" }}
        >
          {connected ? "● Live" : "○ Waiting"}
          {statusLabel ? ` · ${statusLabel}` : ""}
        </span>
      </div>

      {bundle ? (
        <div
          style={{
            padding: mode === "phone" ? "16px 12px" : "18px clamp(14px, 3vw, 28px)",
            maxWidth: mode === "phone" ? 420 : "var(--cave-public-page-width, 1120px)",
            margin: "0 auto",
          }}
        >
          <LobbyRenderer
            editorPreview
            doc={bundle.doc}
            caveName={bundle.caveName}
            legend={bundle.legend}
            caveId={bundle.caveId}
            assets={bundle.assets}
            tickets={bundle.tickets}
            live={[]}
            events={bundle.events}
            eventsStatus="ready"
            owned={owned}
            acquired={new Set()}
            signedIn={holder}
            busyId={null}
            watching={null}
            onBuy={() => undefined}
            onWatch={() => undefined}
            onStopWatch={() => undefined}
            onSignIn={() => undefined}
          />
        </div>
      ) : (
        <div role="status" style={{ padding: 40, textAlign: "center", color: "var(--text-dim)" }}>
          <p style={{ margin: 0, fontSize: "var(--fs-lg)", color: "var(--text-hi)" }}>Waiting for the editor…</p>
          <p style={{ margin: "8px 0 0", fontSize: "var(--fs-body-2)" }}>
            {waitingNote ?? "Keep the Designer open — this window updates as you make changes."}
          </p>
        </div>
      )}
    </main>
  );
}
